import { TENANT_HEADER_NAME } from "@sg-protect/core";
import { useTenantContext } from "@sg-protect/shell";
import { useQuery as useTanstackQuery, useSuspenseQuery as useTanstackSuspenseQuery } from "@tanstack/react-query";
import type { Client } from "openapi-fetch";
import createQueryClient, { type OpenapiQueryClient } from "openapi-react-query";
import type { paths } from "./types.ts";

const MIGRATE_QUERY_SCOPE = "migrate";

function withTenantHeader(headers: HeadersInit | undefined, tenantId: string) {
    const normalized = new Headers(headers);
    normalized.set(TENANT_HEADER_NAME, tenantId);

    return normalized;
}

type MigrateApi = OpenapiQueryClient<paths>;
type ScopedQueryClient = Pick<MigrateApi, "queryOptions" | "useQuery" | "useSuspenseQuery" | "useMutation"> & {
    unscopedQueryOptions: MigrateApi["queryOptions"];
    useUnscopedQuery: MigrateApi["useQuery"];
    useUnscopedSuspenseQuery: MigrateApi["useSuspenseQuery"];
};

type QueryOptionsParams = Parameters<MigrateApi["queryOptions"]>;
type QueryOptionsReturn = ReturnType<MigrateApi["queryOptions"]>;
type UseQueryParams = Parameters<MigrateApi["useQuery"]>;
type UseSuspenseQueryParams = Parameters<MigrateApi["useSuspenseQuery"]>;

export function createTenantScopedQueryClient(client: Client<paths>): ScopedQueryClient {
    const api = createQueryClient(client);

    // The scoped hooks read the active tenant from React state rather than ambient storage,
    // so a client-side project switch re-scopes every query key and pins the request header to
    // the new tenant. Both derive from the same value, keeping cache and request in lockstep.
    function tenantScopedQueryOptions(tenantId: string | null, ...args: QueryOptionsParams): QueryOptionsReturn {
        const [method, path, init, options] = args;

        const initObject = (init ?? {}) as { headers?: HeadersInit };
        const initWithTenant =
            tenantId !== null ? { ...initObject, headers: withTenantHeader(initObject.headers, tenantId) } : init;

        const base = api.queryOptions(method, path, initWithTenant as QueryOptionsParams[2], options);

        const scopedQueryFn = (context: Parameters<typeof base.queryFn>[0]) =>
            base.queryFn({ ...context, queryKey: base.queryKey });

        return {
            ...base,
            queryKey: [MIGRATE_QUERY_SCOPE, tenantId, ...base.queryKey],
            queryFn: scopedQueryFn
        } as QueryOptionsReturn;
    }

    function useTenantScopedQuery(...[method, path, init, options, queryClient]: UseQueryParams) {
        const { currentTenant } = useTenantContext();

        return useTanstackQuery(
            tenantScopedQueryOptions(currentTenant?.tenantId ?? null, method, path, init, options),
            queryClient
        );
    }

    function useTenantScopedSuspenseQuery(...[method, path, init, options, queryClient]: UseSuspenseQueryParams) {
        const { currentTenant } = useTenantContext();

        return useTanstackSuspenseQuery(
            tenantScopedQueryOptions(currentTenant?.tenantId ?? null, method, path, init, options),
            queryClient
        );
    }

    return {
        queryOptions: ((...args: QueryOptionsParams) =>
            tenantScopedQueryOptions(null, ...args)) as MigrateApi["queryOptions"],
        useQuery: useTenantScopedQuery as MigrateApi["useQuery"],
        useSuspenseQuery: useTenantScopedSuspenseQuery as MigrateApi["useSuspenseQuery"],
        useMutation: api.useMutation,
        unscopedQueryOptions: api.queryOptions,
        useUnscopedQuery: api.useQuery,
        useUnscopedSuspenseQuery: api.useSuspenseQuery
    };
}
